import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { trackDataSelector, highlightedDriveSelector, highlightedStopSelector } from '../../../features/datacenter/historySlice';
import { gensetTrackReportIdSelector, gensetViewportPaddingSelector, setNeedBounding } from '../../../features/map/mapSlice';

const GoogleGensetTrackBounder = ({ map }) => {
    const dispatch = useDispatch();
    const reportId = useSelector(gensetTrackReportIdSelector);
    const trackData = useSelector(trackDataSelector);
    const highlightedDrive = useSelector(highlightedDriveSelector);
    const highlightedStop = useSelector(highlightedStopSelector);
    const viewportPadding = useSelector(gensetViewportPaddingSelector);

    useEffect(() => {
        if (!map || !reportId) return;
        const track = trackData?.[reportId]?.track || [];
        if (!track.length || !window.google) return;
        const padding = viewportPadding
            ? {
                left: Math.max(0, viewportPadding.left || 0),
                top: Math.max(0, viewportPadding.top || 0),
                right: Math.max(0, viewportPadding.right || 0),
                bottom: Math.max(0, viewportPadding.bottom || 0),
            }
            : { left: 350, top: 75, right: 25, bottom: 100 };
        const applyOffsetCenter = (lat, lng) => {
            const latlng = new window.google.maps.LatLng(lat, lng);
            if (viewportPadding && map.getProjection && map.getProjection()) {
                const div = map.getDiv();
                const width = div?.offsetWidth || 0;
                const height = div?.offsetHeight || 0;
                const left = Math.max(0, padding.left || 0);
                const top = Math.max(0, padding.top || 0);
                const right = Math.max(0, padding.right || 0);
                const bottom = Math.max(0, padding.bottom || 0);
                const viewportWidth = Math.max(0, width - left - right);
                const viewportHeight = Math.max(0, height - top - bottom);
                const viewportCenterX = left + viewportWidth / 2;
                const viewportCenterY = top + viewportHeight / 2;
                const offsetX = viewportCenterX - width / 2;
                const offsetY = viewportCenterY - height / 2;
                const scale = Math.pow(2, map.getZoom());
                const worldCoordinateCenter = map.getProjection().fromLatLngToPoint(latlng);
                const worldCoordinateNewCenter = new window.google.maps.Point(
                    worldCoordinateCenter.x - (offsetX / scale),
                    worldCoordinateCenter.y - (offsetY / scale)
                );
                map.setCenter(map.getProjection().fromPointToLatLng(worldCoordinateNewCenter));
            } else {
                map.setCenter(latlng);
            }
        };
        if (highlightedDrive?.startIndex !== null && highlightedDrive?.endIndex !== null) {
            const bounds = new window.google.maps.LatLngBounds();
            track.forEach((point, index) => {
                if (index >= highlightedDrive.startIndex && index <= highlightedDrive.endIndex) {
                    bounds.extend(new window.google.maps.LatLng(point.lat, point.lng));
                }
            });
            map.fitBounds(bounds, padding);
            dispatch(setNeedBounding(false));
            return;
        }
        if (highlightedStop?.startIndex !== null) {
            const point = track[highlightedStop.startIndex];
            if (point) {
                applyOffsetCenter(point.lat, point.lng);
                dispatch(setNeedBounding(false));
                return;
            }
        }
        const bounds = new window.google.maps.LatLngBounds();
        track.forEach((point) => {
            bounds.extend(new window.google.maps.LatLng(point.lat, point.lng));
        });
        map.fitBounds(bounds, padding);
        dispatch(setNeedBounding(false));
    }, [dispatch, highlightedDrive, highlightedStop, map, reportId, trackData, viewportPadding]);

    return null;
};

export default GoogleGensetTrackBounder;
